var _a;
import { __extends } from "tslib";
/**
* ApplicationInsights.ts
* @author Ram Thiru (ramthi) and Hector Hernandez (hectorh)
* @copyright Microsoft 2020
* JSLL Wrapper class.
*/
import { AuthPlugin } from "@microsoft/1ds-auth-js";
import { CorrelationVectorPlugin } from "@microsoft/1ds-cv-js";
import { IdSyncPlugin } from "@microsoft/1ds-id-sync-js";
import { PostChannel } from "@microsoft/1ds-post-js";
import { PropertiesPlugin } from "@microsoft/1ds-properties-js";
import { QosPlugin } from "@microsoft/1ds-qos-js";
import { ApplicationInsights as WebAnalyticsPlugin } from "@microsoft/1ds-wa-js";
import { AppInsightsExtCore, _throwInternal, createDynamicConfig, isValueAssigned } from "@microsoft/applicationinsights-core-js";
import { arrAppend, objDeepFreeze, objDefineProps } from "@nevware21/ts-utils";
/**
 * The default settings for the config.
 * WE MUST include all defaults here to ensure that the config is created with all of the properties
 * defined as dynamic.
 */
var defaultConfig = objDeepFreeze({
    cookieCfg: { ref: true, v: {} },
    extensions: { rdOnly: true, ref: true, v: [] },
    channels: { rdOnly: true, ref: true, v: [] },
    featureOptIn: (_a = {},
        _a["zipPayload"] = { mode: 1 },
        _a),
    extensionConfig: { ref: true, v: {} }
});
// this class represents the ClickstreamTracker class in JSLL
var ApplicationInsights = /** @class */ (function (_super) {
    __extends(ApplicationInsights, _super);
    function ApplicationInsights() {
        var _this = _super.call(this) || this;
        _this._postChannel = new PostChannel();
        _this._propertyManager = new PropertiesPlugin();
        _this._webAnalytics = new WebAnalyticsPlugin();
        _this._qosPlugin = new QosPlugin();
        _this._cvPlugin = new CorrelationVectorPlugin();
        _this._authPlugin = new AuthPlugin();
        _this._idSyncPlugin = new IdSyncPlugin();
        return _this;
    }
    /**
     * Initialize the SKU.
     * @param config        - SKU configuration.
     * @param extensions    - An array of extensions that are to be used by the core.
     */
    ApplicationInsights.prototype.initialize = function (config, extensions) {
        var _self = this;
        config = createDynamicConfig(config, defaultConfig, _self.logger, false).cfg;
        _self._config = config;
        arrAppend(config.extensions, [_self._propertyManager, _self._webAnalytics, _self._qosPlugin, _self._cvPlugin]);
        if (_self._config.jsllConfiguration && _self._config.jsllConfiguration.shareAuthStatus) {
            arrAppend(config.extensions, _self._authPlugin);
        }
        if (_self._config.jsllConfiguration && _self._config.jsllConfiguration.ix) {
            arrAppend(config.extensions, _self._idSyncPlugin);
        }
        if (extensions) {
            arrAppend(config.extensions, extensions);
        }
        if (config.channels && config.channels.length > 0) {
            // Add post channel to first fork if not available
            var postFound = false;
            for (var j = 0; j < config.channels[0].length; j++) {
                if (config.channels[0][j].identifier === _self._postChannel.identifier) {
                    postFound = true;
                    break;
                }
            }
            if (!postFound) {
                arrAppend(config.channels[0], _self._postChannel);
            }
        }
        else {
            // Push the subsequent channels
            config.channels.push([_self._postChannel]);
        }
        _self.initializeConfigurations();
        try {
            _super.prototype.initialize.call(this, config, []);
            // TODO: Properties context and logger cannot be setup before initialization, update when supported
            if (_self._config.jsllConfiguration) {
                if (_self._config.jsllConfiguration.coreData && isValueAssigned(_self._config.jsllConfiguration.coreData.appId)) {
                    _self._propertyManager.getPropertiesContext().app.id = _self._config.jsllConfiguration.coreData.appId;
                }
                if (isValueAssigned(_self._config.jsllConfiguration.logLevel)) {
                    _self.logger.consoleLoggingLevel = function () { return _self._config.jsllConfiguration.logLevel; };
                }
            }
            if (_self.isInitialized()) {
                // Update the shared config to map the common passed values
                objDefineProps(config, {
                    channelConfiguration: { g: function () { return config.extensionConfig[_self._postChannel.identifier]; } },
                    propertyConfiguration: { g: function () { return config.extensionConfig[_self._propertyManager.identifier]; } },
                    webAnalyticsConfiguration: { g: function () { return config.extensionConfig[_self._webAnalytics.identifier]; } },
                    qosConfiguration: { g: function () { return config.extensionConfig[_self._qosPlugin.identifier]; } },
                    idSyncConfiguration: { g: function () { return config.extensionConfig[_self._idSyncPlugin.identifier]; } },
                    authConfiguration: { g: function () { return config.extensionConfig[_self._authPlugin.identifier]; } }
                });
            }
        }
        catch (error) {
            _throwInternal(_self.logger, 1 /* eLoggingSeverity.CRITICAL */, 514 /* _eExtendedInternalMessageId.FailedToInitializeSDK */, "Failed to initialize SDK." + error);
        }
    };
    /**
     * Gets the property manager to set custom properties and system properties (part A), that should be applied
     * to all events or events with a specific instrumentation key.
     * @returns {PropertiesPlugin} The property manager object.
     */
    ApplicationInsights.prototype.getPropertyManager = function () {
        return this._propertyManager;
    };
    /**
     * Gets the property manager configuration.
     * @returns {IPropertyConfiguration} The property manager configuration.
     */
    ApplicationInsights.prototype.getPropertyManagerConfig = function () {
        var extConfig = this._config.extensionConfig || [];
        return extConfig[this._propertyManager.identifier];
    };
    /**
     * Gets the post channel to configure and set the transmission profiles.
     * @returns {PostChannel} The post channel object.
     */
    ApplicationInsights.prototype.getPostChannel = function () {
        return this._postChannel;
    };
    /**
     * Gets the post channel configuration.
     * @returns {IChannelConfiguration} The post channel configuration.
     */
    ApplicationInsights.prototype.getPostChannelConfig = function () {
        var extConfig = this._config.extensionConfig || [];
        return extConfig[this._postChannel.identifier];
    };
    /**
     * Gets the Web Analytics extension.
     * @returns {WebAnalytics} The Web Analytics extension.
     */
    ApplicationInsights.prototype.getWebAnalyticsExtension = function () {
        return this._webAnalytics;
    };
    /**
     * Gets the web analytics extension configuration.
     * @returns {IWebAnalyticsConfiguration} The web analytics extension configuration.
     */
    ApplicationInsights.prototype.getWebAnalyticsExtensionConfig = function () {
        var extConfig = this._config.extensionConfig || [];
        return extConfig[this._webAnalytics.identifier];
    };
    /**
     * Gets the Qos extension.
     * @returns {QosPlugin} The Qos extension.
     */
    ApplicationInsights.prototype.getQosExtension = function () {
        return this._qosPlugin;
    };
    /**
     * Gets the Qos extension configuration.
     * @returns {IQosConfiguration} The Qos extension configuration.
     */
    ApplicationInsights.prototype.getQosExtensionConfig = function () {
        var extConfig = this._config.extensionConfig || [];
        return extConfig[this._qosPlugin.identifier];
    };
    /**
     * Gets the Auth extension.
     * @returns {AuthPlugin} The Auth extension.
     */
    ApplicationInsights.prototype.getAuthExtension = function () {
        return this._authPlugin;
    };
    /**
     * Gets the Auth extension configuration.
     * @returns {IAuthenticationConfiguration} The Auth extension configuration.
     */
    ApplicationInsights.prototype.getAuthExtensionConfig = function () {
        var extConfig = this._config.extensionConfig || [];
        return extConfig[this._authPlugin.identifier];
    };
    /**
     * Gets the Correlation Vector extension.
     * This function is deprecated, will be removed after v4.4.0
     * @deprecated
     * @returns {CorrelationVectorPlugin} The Correlation Vector extension.
     */
    ApplicationInsights.prototype.getCvExtension = function () {
        return this._cvPlugin;
    };
    /**
     * Gets the Id Sync extension.
     * @returns {IdSyncPlugin} The ID Sync extension.
     */
    ApplicationInsights.prototype.getIdSyncExtension = function () {
        return this._idSyncPlugin;
    };
    /**
     * Gets the Id Sync extension configuration.
     * @returns {IIdSyncConfiguration} The Id Sync extension configuration.
     */
    ApplicationInsights.prototype.getIdSyncExtensionConfig = function () {
        var extConfig = this._config.extensionConfig || [];
        return extConfig[this._idSyncPlugin.identifier];
    };
    ApplicationInsights.prototype.initializeConfigurations = function () {
        var _self = this;
        // Add configurations
        var extConfig = _self._config.extensionConfig = _self._config.extensionConfig || [];
        extConfig[_self._qosPlugin.identifier] = _self._config.qosConfiguration || {};
        extConfig[_self._postChannel.identifier] = _self._config.channelConfiguration || {};
        var propertiesConfig = extConfig[_self._propertyManager.identifier] = _self._config.propertyConfiguration || {};
        var webAnalyticsConfig = extConfig[_self._webAnalytics.identifier] = _self._config.webAnalyticsConfiguration || {};
        var qosConfig = extConfig[_self._qosPlugin.identifier] = _self._config.qosConfiguration || {};
        var idSyncConfig = extConfig[_self._idSyncPlugin.identifier] = _self._config.idSyncConfiguration || {};
        var authConfig = extConfig[_self._authPlugin.identifier] = _self._config.authConfiguration || { authType: 0, loggedInStatusCallback: function () { return false; } };
        var jsllConfig = _self._config.jsllConfiguration;
        // JSLL config will override if conflicting config is provided
        if (jsllConfig) {
            _self._config.endpointUrl = jsllConfig.endpoint;
            // Qos config
            qosConfig.disableAjaxTracking = !jsllConfig.enableAjaxTracking;
            qosConfig.disableFetchTracking = !jsllConfig.enableFetchTracking;
            if (jsllConfig.autoCapture) {
                webAnalyticsConfig.autoCapture = webAnalyticsConfig.autoCapture || {};
                webAnalyticsConfig.autoCapture.click = jsllConfig.autoCapture.click;
                webAnalyticsConfig.autoCapture.jsError = jsllConfig.autoCapture.jsError;
                webAnalyticsConfig.autoCapture.lineage = jsllConfig.autoCapture.lineage;
                webAnalyticsConfig.autoCapture.onLoad = jsllConfig.autoCapture.onLoad;
                webAnalyticsConfig.autoCapture.onUnload = jsllConfig.autoCapture.onUnload;
                webAnalyticsConfig.autoCapture.pageView = jsllConfig.autoCapture.pageView;
                webAnalyticsConfig.autoCapture.resize = jsllConfig.autoCapture.resize;
                webAnalyticsConfig.autoCapture.scroll = jsllConfig.autoCapture.scroll;
                webAnalyticsConfig.autoCapture.msTags = jsllConfig.autoCapture.msTags;
                qosConfig.autoCapture = qosConfig.autoCapture || {};
                qosConfig.autoCapture.msTags = jsllConfig.autoCapture.msTags;
                // addin, perf, assets, invalidEvents, awaTags, eventTooLong, corsDisallowed not supported
            }
            if (jsllConfig.ix) {
                idSyncConfig.adobe = jsllConfig.ix.a;
                idSyncConfig.google = jsllConfig.ix.g;
            }
            if (jsllConfig.coreData) {
                webAnalyticsConfig.coreData = webAnalyticsConfig.coreData || {};
                webAnalyticsConfig.coreData.market = jsllConfig.coreData.market;
                webAnalyticsConfig.coreData.pageName = jsllConfig.coreData.pageName;
                webAnalyticsConfig.coreData.pageTags = jsllConfig.coreData.pageTags;
                webAnalyticsConfig.coreData.pageType = jsllConfig.coreData.pageType;
                webAnalyticsConfig.coreData.product = jsllConfig.coreData.product;
                webAnalyticsConfig.coreData.referrerUri = jsllConfig.coreData.referrerUri;
                webAnalyticsConfig.coreData.requestUri = jsllConfig.coreData.requestUri;
                propertiesConfig.env = jsllConfig.coreData.env;
                propertiesConfig.expId = jsllConfig.coreData.expId;
                propertiesConfig.serviceName = jsllConfig.coreData.serviceName;
                // title,  serverImpressionGuid not supported
            }
            if (jsllConfig.callback) {
                webAnalyticsConfig.callback = webAnalyticsConfig.callback || {};
                webAnalyticsConfig.callback.contentUpdatePageTags = jsllConfig.callback.contentUpdatePageTags;
                webAnalyticsConfig.callback.pageActionContentTags = jsllConfig.callback.pageActionContentTags;
                webAnalyticsConfig.callback.pageActionPageTags = jsllConfig.callback.pageActionPageTags;
                webAnalyticsConfig.callback.pageName = jsllConfig.callback.pageName;
                webAnalyticsConfig.callback.pageViewPageTags = jsllConfig.callback.pageViewPageTags;
                webAnalyticsConfig.callback.signedinStatus = jsllConfig.callback.signedinStatus;
                authConfig.loggedInStatusCallback = jsllConfig.callback.signedinStatus;
                propertiesConfig.callback = propertiesConfig.callback || {};
                propertiesConfig.callback.userConsentDetails = jsllConfig.callback.userConsentDetailsCallback;
                // jsllEventCallback and pageUnloadTimings not supported
            }
            if (jsllConfig.debounceMs) {
                webAnalyticsConfig.debounceMs = webAnalyticsConfig.debounceMs || {};
                webAnalyticsConfig.debounceMs.scroll = jsllConfig.debounceMs.scroll;
                webAnalyticsConfig.debounceMs.resize = jsllConfig.debounceMs.resize;
            }
            webAnalyticsConfig.cookiesToCollect = jsllConfig.cookiesToCollect;
            webAnalyticsConfig.mscomCookies = jsllConfig.mscomCookies;
            webAnalyticsConfig.urlCollectHash = jsllConfig.urlCollectHash;
            webAnalyticsConfig.urlCollectQuery = jsllConfig.urlCollectQuery;
            webAnalyticsConfig.useDefaultContentName = jsllConfig.useDefaultContentName;
            webAnalyticsConfig.useShortNameForContentBlob = jsllConfig.useShortNameForContentBlob;
            webAnalyticsConfig.biBlobAttributeTag = jsllConfig.biBlobAttributeTag;
            webAnalyticsConfig.useShortNameForContentBlob = jsllConfig.useShortNameForContentBlob;
            webAnalyticsConfig.isLoggedIn = jsllConfig.isLoggedIn;
            webAnalyticsConfig.manualPageUnload = jsllConfig.manualPageUnload;
            webAnalyticsConfig.syncMuid = jsllConfig.syncMuid;
            webAnalyticsConfig.muidDomain = jsllConfig.muidDomain;
            propertiesConfig.userConsentCookieName = jsllConfig.userConsentCookieName;
            propertiesConfig.userConsented = jsllConfig.userConsented;
            // TODO: Remove as part of task [1P] Remove shareAuthStatus config duplication(7393950)
            webAnalyticsConfig.shareAuthStatus = jsllConfig.shareAuthStatus;
            if (jsllConfig.authMethod) {
                authConfig.authType = jsllConfig.authMethod;
            }
            // initCv not supportedm cV is always initialized in 1DS
            // sendMode, validateEvents, provisionMsfpc, sendEvents, enabledFeatures and batchSize not supported
        }
    };
    return ApplicationInsights;
}(AppInsightsExtCore));
export { ApplicationInsights };
//# sourceMappingURL=ApplicationInsights.js.map